import { FileButton } from '../components/FileButton';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TASKS, canOperate, customerPill, hasFeature, cycleResultLabel, type WorkstationServiceStatus } from '@visionqc/shared';
import { useAuth } from '../api/auth';
import { ByoaGuide } from '../components/ByoaGuide';
import { ServiceStatusPill } from '../components/ServiceStatusPill';
import { ProcGuardKioskView } from './ProcGuardKioskView';
import { SafetyScenarioWizard } from './SafetyScenarioWizard';
import { SafetyActivityPanel } from '../components/SafetyActivity';
import { SafetySiteLeaderboard } from '../components/SafetyLeaderboard';
import {
  CameraConnection,
  CameraPicker,
  LiveZonesView,
  ZoneEditor,
  addSafetyCamera,
  isLiveSource,
  snapshotUrl,
} from '../components/liveCameras';

/**
 * What a customer sees. Their own line, and nothing else.
 *
 * A separate shell rather than the staff app with pieces hidden, because
 * hiding is a promise the UI makes and the server has to keep. Everything here
 * reads /api/portal/*, which is the only family a CUSTOMER session can reach
 * (see the allowlist in routes/auth.ts) and which runs under row-level security
 * on a role that cannot bypass it. Two independent refusals, neither relying on
 * this file being careful.
 *
 * SPLIT BY TASK, like everywhere else. A pass rate and an agreement rate have
 * no common unit; a customer running both gets both, never one blended number.
 */

interface PortalMe {
  id: number;
  name: string;
  colorHue: number | null;
  hasLogo?: boolean;
}

interface PortalProduct {
  id: number;
  name: string;
  task: 'anomaly' | 'classification';
  total: number;
  anomaly: { good: number; bad: number; unsure: number } | null;
  classification: { classified: number; rejected: number; corrected: number } | null;
}

interface PortalInspection {
  id: string;
  time: string;
  productName: string;
  task: string;
  verdict: string | null;
  score: number | null;
  imageKey: string | null;
  review: { label: string | null; defect: string | null; defectColor: string | null } | null;
}

interface PortalOverview {
  window: string;
  products: PortalProduct[];
  stations: Array<{ id: number; name: string; active: boolean; lastSeen: string | null }>;
}

interface PortalTrend {
  window: string;
  bucket: 'hour' | 'day';
  buckets: Array<{ t: string; good: number; bad: number; unsure: number }>;
}

const WINDOWS = [
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
];

export function PortalView() {
  const { user, logout, refresh } = useAuth();
  const [me, setMe] = useState<PortalMe | null>(null);
  const [window_, setWindow] = useState('7d');
  const [data, setData] = useState<PortalOverview | null>(null);
  const [trend, setTrend] = useState<PortalTrend | null>(null);
  const [recent, setRecent] = useState<PortalInspection[]>([]);
  const [changing, setChanging] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /*
    What this tenant is entitled to. The session carries the grants (from
    /api/auth/me), so the portal shows exactly the functions the customer was
    given and fetches only those — a section that is off is never requested, so
    the backend's own per-feature refusal (see requireFeature in portal.ts)
    stays a guard, not a source of visible 403s. `null` = unrestricted, which a
    CUSTOMER never is, so this only ever loosens for staff, who cannot reach here.
  */
  const features = user?.features ?? null;
  const can = useCallback((key: string) => hasFeature(features, key), [features]);
  const canDashboard = can('dashboard');
  const canLive = can('live');
  const canLabels = can('labels');
  const canSafety = can('safety');
  const canReview = canOperate(features, 'quality');
  const hasAnyFunction = canDashboard || canLive || canLabels || canSafety;

  const canQuality = can('quality');
  const canClassification = can('classification');
  const canProcguard = can('procguard');

  // Left-nav sections: Home (the launcher) first, then only the functions this
  // tenant is entitled to, then the always-present account areas.
  const SECTIONS = useMemo(
    () =>
      [
        { id: 'home', label: 'Home', icon: '⌂', show: true },
        { id: 'overview', label: 'Dashboard', icon: '▦', show: canDashboard },
        { id: 'live', label: 'Live', icon: '●', show: canLive },
        { id: 'quality', label: 'Quality', icon: '✓', show: canQuality },
        { id: 'labels', label: 'Etiket AI', icon: '▤', show: canLabels },
        { id: 'classification', label: 'Classification', icon: '⊜', show: canClassification },
        { id: 'safety', label: 'Safety', icon: '⛑', show: canSafety },
        { id: 'jakatori', label: 'Jakatori', icon: '◈', show: canProcguard },
        { id: 'team', label: 'Team', icon: '⚇', show: true },
        { id: 'agents', label: 'Agents', icon: '✦', show: true },
      ].filter((s) => s.show),
    [canDashboard, canLive, canQuality, canLabels, canClassification, canSafety, canProcguard],
  );
  // Land on Home (the launcher) at sign-in.
  const [section, setSection] = useState('home');
  useEffect(() => {
    setSection((cur) => (cur && SECTIONS.some((s) => s.id === cur) ? cur : SECTIONS[0]?.id ?? 'home'));
  }, [SECTIONS]);

  const reviewInspection = async (id: string, label: 'GOOD' | 'BAD' | 'UNSURE') => {
    try {
      const res = await fetch(`/api/portal/inspections/${id}/review`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ label }),
      });
      if (res.ok) {
        setRecent((rs) =>
          rs.map((r) =>
            r.id === id
              ? { ...r, review: { label, defect: r.review?.defect ?? null, defectColor: r.review?.defectColor ?? null } }
              : r,
          ),
        );
      }
    } catch {
      /* leave it; the operator can retry */
    }
  };

  // A portal session lasts 12 hours; when it lapses while a tab is open, every
  // call starts returning 401. Re-check auth so the app drops to the sign-in
  // screen (App.tsx renders LoginView once the user goes null) instead of
  // showing confusing "could not load" errors and polling 401s forever.
  const onExpired = useCallback(() => { void refresh(); }, [refresh]);

  useEffect(() => {
    fetch('/api/portal/me')
      .then((r) => {
        if (r.status === 401) { onExpired(); return null; }
        return r.ok ? r.json() : Promise.reject(new Error('Could not load your account'));
      })
      .then((v) => { if (v) setMe(v); })
      .catch((e: Error) => setError(e.message));
  }, [onExpired]);

  const load = useCallback(() => {
    if (!canDashboard) return;
    fetch(`/api/portal/overview?window=${window_}`)
      .then((r) => {
        if (r.status === 401) { onExpired(); return null; }
        return r.ok ? r.json() : Promise.reject(new Error('Could not load your figures'));
      })
      .then((d) => {
        if (!d) return;
        setData(d);
        setError(null);
      })
      .catch((e: Error) => setError(e.message));
    fetch(`/api/portal/trend?window=${window_}`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setTrend)
      .catch(() => setTrend(null));
  }, [window_, canDashboard, onExpired]);

  useEffect(() => {
    load();
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    if (!canLive) return;
    fetch('/api/portal/inspections?limit=12')
      .then((r) => (r.ok ? r.json() : []))
      .then(setRecent)
      .catch(() => setRecent([]));
  }, [window_, canLive]);

  const pill = customerPill(me?.colorHue);

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '28px 24px' }}>
      {/* A calm brand strip in the customer's own tint — colour brought in
          quietly, the way the rest of the app does it. */}
      <div style={{ height: 4, background: pill.border, borderRadius: 4, marginBottom: 20 }} />
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 24 }}>
        <div className="row" style={{ gap: 14 }}>
          {me?.hasLogo ? (
            // Their mark leads; ours steps back to a quiet endorsement.
            <>
              <img
                src="/api/portal/logo"
                alt={me.name}
                style={{ height: 36, maxWidth: 200, objectFit: 'contain', display: 'block' }}
              />
              <span className="muted" style={{ fontSize: 11 }}>
                on the<span style={{ color: 'var(--accent)' }}>vision</span>qc
              </span>
            </>
          ) : (
            <>
              <span style={{ fontSize: 20, fontWeight: 800 }}>
                the<span style={{ color: 'var(--accent)' }}>vision</span>qc
              </span>
              {me && (
                <span
                  style={{
                    padding: '4px 12px',
                    borderRadius: 999,
                    background: pill.background,
                    color: pill.text,
                    border: `1px solid ${pill.border}`,
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  {me.name}
                </span>
              )}
            </>
          )}
        </div>
        <div className="row" style={{ gap: 12 }}>
          <span className="muted" style={{ fontSize: 13 }}>{user?.name}</span>
          <button className="btn sm" onClick={() => setChanging((c) => !c)}>
            {changing ? 'Cancel' : 'Change password'}
          </button>
          <button className="btn sm" onClick={() => void logout()}>
            Sign out
          </button>
        </div>
      </div>

      {error && <div className="panel" style={{ color: 'var(--bad)', marginBottom: 16 }}>{error}</div>}

      {/*
        A portal password arrives from whoever set the account up, which means
        somebody else knows it. Without this the customer can never make it
        theirs — and change-password was already on the allowlist, so the
        endpoint was reachable with nothing offering it.
      */}
      {changing && <ChangePassword onDone={(m) => { setChanging(false); setNotice(m); }} />}
      {notice && (
        <div className="panel" style={{ color: 'var(--good)', marginBottom: 16 }}>{notice}</div>
      )}

      {/*
        A tenant with no functions turned on is not an error — it is a customer
        set up but not yet switched on. Say so plainly rather than showing an
        empty page that reads as broken.
      */}
      {!hasAnyFunction && (
        <div className="panel muted">
          Your account is set up, but no functions are enabled yet. Contact us to switch on the
          parts of the platform you need.
        </div>
      )}

      {/* Portal shell: a left nav to pick a function, and the pane beside it.
          Always shown — Team and Agents exist even for a tenant with no functions. */}
      {SECTIONS.length > 0 && (
      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <nav
          className="panel"
          style={{ flex: '0 0 190px', padding: 8, position: 'sticky', top: 16, display: 'flex', flexDirection: 'column', gap: 4 }}
        >
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setSection(s.id)}
              style={{
                textAlign: 'left', padding: '10px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                background: section === s.id ? 'var(--accent)' : 'transparent',
                color: section === s.id ? '#fff' : 'var(--text)', fontWeight: 600, fontSize: 14,
              }}
            >
              <span className="nav-ico" style={{ marginRight: 8 }} aria-hidden>{s.icon}</span>{s.label}
            </button>
          ))}
        </nav>
        <div style={{ flex: '1 1 320px', minWidth: 0 }}>

      {section === 'home' && (
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 800, marginBottom: 4 }}>Welcome{me ? `, ${me.name}` : ''}</h2>
          <p className="muted" style={{ marginTop: 0, marginBottom: 16 }}>Choose a service to start.</p>
          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 }}>
            {SECTIONS.filter((s) => s.id !== 'home').map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setSection(s.id)}
                className="panel"
                style={{ textAlign: 'left', cursor: 'pointer', border: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}
              >
                <span className="nav-ico" style={{ fontSize: 30, color: 'var(--accent)' }} aria-hidden>{s.icon}</span>
                <span style={{ fontSize: 17, fontWeight: 700 }}>{s.label}</span>
                <span style={{ color: 'var(--accent)', fontSize: 13, fontWeight: 700 }}>Start →</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {(section === 'overview' || section === 'live') && (
        <div className="panel" style={{ marginBottom: 20 }}>
          <label>Time window</label>
          <select value={window_} onChange={(e) => setWindow(e.target.value)}>
            {WINDOWS.map((w) => (
              <option key={w.value} value={w.value}>
                {w.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {section === 'overview' && canDashboard && trend && trend.buckets.some((b) => b.good + b.bad + b.unsure > 0) && (
        <TrendChart trend={trend} />
      )}

      {section === 'overview' && canDashboard &&
        (!data ? (
          !error && <div className="panel spinner">Loading…</div>
        ) : data.products.length === 0 ? (
          <div className="panel muted">Nothing has run yet.</div>
        ) : (
          <div className="grid" style={{ gap: 14 }}>
            {data.products.map((p) => (
              <ProductPanel key={p.id} product={p} />
            ))}
          </div>
        ))}

      {section === 'overview' && canDashboard && <PortalQualityAnalytics />}

      {section === 'live' && canLive && <PortalImageDirectory />}

      {section === 'overview' && canDashboard && <PortalBatches />}

      {section === 'live' && canLive && <PortalDiagnostic />}

      {section === 'labels' && canLabels && <PortalLabels />}

      {section === 'safety' && canSafety && <PortalSafety />}

      {section === 'quality' && canQuality && <PortalQualityAnalytics />}

      {section === 'classification' && canClassification && <PortalClassification />}

      {section === 'jakatori' && canProcguard && (
        <PortalJakatori canOperate={canOperate(features, 'procguard')} />
      )}

      {section === 'live' && canLive && recent.length > 0 && (
        <div className="panel" style={{ marginTop: 20 }}>
          <strong>Recent inspections</strong>
          <div
            className="grid"
            style={{
              gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
              gap: 12,
              marginTop: 12,
            }}
          >
            {recent.map((i) => (
              <div key={i.id} className="stat-tile" style={{ padding: 8 }}>
                {i.imageKey && (
                  /*
                    /api/portal/image/* — never /api/images/*. The staff route
                    streams whatever key it is handed; this one looks the key up
                    under row-level security first and 404s anything that is not
                    this customer's.
                  */
                  <img
                    src={`/api/portal/image/${i.imageKey}`}
                    alt=""
                    style={{
                      width: '100%',
                      height: 90,
                      objectFit: 'cover',
                      borderRadius: 6,
                      display: 'block',
                    }}
                  />
                )}
                <div className="muted" style={{ fontSize: 10, marginTop: 6 }}>
                  {new Date(i.time).toLocaleString()}
                </div>
                {i.verdict && <Verdict label={i.verdict} />}

                {/*
                  A person's review, shown as its own fact rather than folded
                  into the verdict. The line's decision is what physically
                  happened to the part; the review is what somebody concluded
                  afterwards. On 41 inspections here the two differ, and
                  printing a human's defect name under the model's GOOD would
                  read as "passed, but cracked" — which neither of them said.
                */}
                {i.review ? (
                  <div style={{ marginTop: 4 }}>
                    {i.review.label && i.review.label !== i.verdict && (
                      <div className="muted" style={{ fontSize: 10 }}>
                        reviewed: <Verdict label={i.review.label} inline />
                      </div>
                    )}
                    {i.review.defect && (
                      <div className="row" style={{ gap: 5, marginTop: 2 }}>
                        <span
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: 2,
                            background: i.review.defectColor ?? 'var(--text-dim)',
                            flex: 'none',
                          }}
                        />
                        <span style={{ fontSize: 11 }}>{i.review.defect}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="muted" style={{ fontSize: 10, marginTop: 4 }}>
                    not reviewed
                  </div>
                )}

                {/* Your own review — confirm or dispute the verdict. Quality
                    inspections only, and only with operate-level access. */}
                {canReview && i.task === 'anomaly' && (
                  <div className="row" style={{ gap: 4, marginTop: 6 }}>
                    {(['GOOD', 'BAD', 'UNSURE'] as const).map((l) => (
                      <button
                        key={l}
                        className="btn sm"
                        style={{ padding: '2px 6px', fontSize: 10, flex: 1 }}
                        title={`Mark ${l}`}
                        onClick={() => void reviewInspection(i.id, l)}
                      >
                        {l === 'GOOD' ? '✓' : l === 'BAD' ? '✕' : '?'}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {section === 'team' && <Colleagues />}

      {section === 'agents' && <PortalAgents />}

      {section === 'overview' && canDashboard && data && data.stations.length > 0 && (
        <div className="panel" style={{ marginTop: 20 }}>
          <strong>Your stations</strong>
          <table style={{ marginTop: 10 }}>
            <tbody>
              {data.stations.map((s) => (
                <tr key={s.id} style={{ cursor: 'default' }}>
                  <td>{s.name}</td>
                  <td className="muted" style={{ fontSize: 13 }}>
                    {s.active ? 'in service' : 'out of service'}
                  </td>
                  <td className="muted" style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>
                    {s.lastSeen ? `last seen ${new Date(s.lastSeen).toLocaleString()}` : 'never seen'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
        </div>
      </div>
      )}
    </div>
  );
}

/**
 * Jakatori (process verification) — the tenant's read-only view of its own
 * workstations, cycle record and analytics. Authoring and the live operator
 * screen stay admin-side for now; this shows the customer how their lines are
 * doing. All three endpoints are tenant-scoped on the server.
 */
function PortalJakatori({ canOperate: mayOperate }: { canOperate: boolean }) {
  interface WS { id: number; name: string; site: string; line: string; enabled: boolean; cameras: unknown[] }
  interface Cyc { id: number; status: string; operator: string | null; startedAt: string; durationMs: number | null }
  interface An { totals: { cycles: number }; compliancePct: number; firstTimeRightPct: number; deviationRate: number; avgDurationMs: number | null }
  const [ws, setWs] = useState<WS[]>([]);
  const [an, setAn] = useState<An | null>(null);
  const [cycles, setCycles] = useState<Cyc[]>([]);
  const [operating, setOperating] = useState<number | null>(null);
  const [statuses, setStatuses] = useState<Record<number, WorkstationServiceStatus>>({});
  useEffect(() => {
    fetch('/api/portal/procguard/workstations').then((r) => (r.ok ? r.json() : [])).then(setWs).catch(() => undefined);
    fetch('/api/portal/procguard/analytics?window=30d').then((r) => (r.ok ? r.json() : null)).then(setAn).catch(() => undefined);
    fetch('/api/portal/procguard/cycles?limit=20').then((r) => (r.ok ? r.json() : [])).then(setCycles).catch(() => undefined);
  }, []);

  // Poll the real service state for every workstation (not just the stored
  // enabled flag) so the customer sees whether verification is actually running.
  const loadStatuses = useCallback(() => {
    ws.forEach((w) => {
      fetch(`/api/portal/procguard/workstations/${w.id}/status`)
        .then((r) => (r.ok ? r.json() : null))
        .then((s: WorkstationServiceStatus | null) => {
          if (s) setStatuses((m) => ({ ...m, [w.id]: s }));
        })
        .catch(() => undefined);
    });
  }, [ws]);
  useEffect(() => {
    loadStatuses();
    const t = setInterval(loadStatuses, 5000);
    return () => clearInterval(t);
  }, [loadStatuses]);

  const toggleActivation = async (id: number, enabled: boolean) => {
    const res = await fetch(`/api/portal/procguard/workstations/${id}/activation`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ enabled }),
    });
    if (res.ok) {
      const s = (await res.json()) as WorkstationServiceStatus;
      setStatuses((m) => ({ ...m, [id]: s }));
      setWs((list) => list.map((w) => (w.id === id ? { ...w, enabled } : w)));
    }
  };
  const tone: Record<string, string> = { ok: 'var(--good)', nok: 'var(--bad)', aborted: 'var(--warn)', running: 'var(--accent)' };

  // Launch the tenant's own live operator/assessment screen for a workstation.
  if (operating != null) {
    return (
      <ProcGuardKioskView
        apiBase="/api/portal/procguard"
        snapshotBase="/api/portal/safety"
        workstationId={operating}
        onExit={() => setOperating(null)}
      />
    );
  }

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 800, marginTop: 0 }}>Jakatori — process verification</h2>
      {an && an.totals.cycles > 0 && (
        <div className="row" style={{ gap: 16, alignItems: 'stretch', flexWrap: 'wrap', marginBottom: 16 }}>
          {[
            { l: 'Process compliance', v: `${an.compliancePct}%`, t: 'var(--good)' },
            { l: 'First-time-right', v: `${an.firstTimeRightPct}%`, t: 'var(--accent)' },
            { l: 'Deviation rate', v: an.deviationRate.toFixed(2), t: 'var(--warn)' },
            { l: 'Avg cycle time', v: an.avgDurationMs != null ? `${(an.avgDurationMs / 1000).toFixed(1)}s` : '—', t: 'var(--teal)' },
          ].map((x) => (
            <div key={x.l} className="stat-tile" style={{ flex: '1 1 150px', ['--tone' as string]: x.t }}>
              <div className="stat-value">{x.v}</div>
              <div className="stat-label">{x.l}</div>
            </div>
          ))}
        </div>
      )}
      <div className="panel" style={{ marginBottom: 16 }}>
        <strong>Workstations</strong>
        {ws.length === 0 ? (
          <div className="muted" style={{ fontSize: 13, marginTop: 8 }}>No workstations set up yet — contact us to configure your workstations.</div>
        ) : (
          <table style={{ width: '100%', marginTop: 8 }}>
            <tbody>
              {ws.map((w) => (
                <tr key={w.id}>
                  <td>{w.name}</td>
                  <td className="muted" style={{ fontSize: 13 }}>{[w.site, w.line].filter(Boolean).join(' · ')}</td>
                  <td className="muted" style={{ fontSize: 12 }}>{w.cameras.length} cam{w.cameras.length === 1 ? '' : 's'}</td>
                  <td><ServiceStatusPill status={statuses[w.id] ?? null} /></td>
                  <td style={{ textAlign: 'right' }}>
                    <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                      {mayOperate && (
                        <button
                          className={`btn sm ${(statuses[w.id]?.enabled ?? w.enabled) ? '' : 'primary'}`}
                          onClick={() => void toggleActivation(w.id, !(statuses[w.id]?.enabled ?? w.enabled))}
                        >
                          {(statuses[w.id]?.enabled ?? w.enabled) ? 'Deactivate' : 'Activate'}
                        </button>
                      )}
                      <button className="btn sm" onClick={() => setOperating(w.id)}>Operator screen</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div className="panel">
        <strong>Recent cycles</strong>
        {cycles.length === 0 ? (
          <div className="muted" style={{ fontSize: 13, marginTop: 8 }}>No cycles recorded yet.</div>
        ) : (
          <table style={{ width: '100%', marginTop: 8, fontVariantNumeric: 'tabular-nums' }}>
            <thead><tr><th>Started</th><th>Result</th><th>Operator</th><th>Duration</th></tr></thead>
            <tbody>
              {cycles.map((c) => (
                <tr key={c.id}>
                  <td className="muted" style={{ fontSize: 13 }}>{new Date(c.startedAt).toLocaleString()}</td>
                  <td><span className="badge" style={{ ['--tone' as string]: tone[c.status], color: tone[c.status] }}>{cycleResultLabel(c.status)}</span></td>
                  <td>{c.operator ?? '—'}</td>
                  <td className="muted">{c.durationMs != null ? `${(c.durationMs / 1000).toFixed(1)}s` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

interface PortalLabelRow {
  id: string;
  time: string;
  productName: string;
  verdict: 'GOOD' | 'BAD' | 'UNSURE';
  aligned: boolean;
  alignScore: number;
  reason: string | null;
}

/**
 * Etiket AI, in the portal — the customer's own label checks.
 *
 * Read-only, like everything else here: this shows what the label inspection
 * decided, per product, under the same row-level security as the rest. The
 * label tables scope by product_id, which the tenant policy already covers, so
 * this reaches exactly this customer's checks and no others. Running a check
 * from the portal (operate) is Phase 2b.
 */
function PortalLabels() {
  const { user } = useAuth();
  const canRun = canOperate(user?.features ?? null, 'labels');
  const [rows, setRows] = useState<PortalLabelRow[]>([]);
  const [loaded, setLoaded] = useState(false);

  const loadRows = useCallback(() => {
    fetch('/api/portal/labels?limit=20')
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => {
        setRows(d);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  return (
    <div className="panel" style={{ marginTop: 20 }}>
      <strong>Etiket AI — label checks</strong>
      {canRun && <LabelRunner onDone={loadRows} />}
      {rows.length === 0 ? (
        <p className="muted" style={{ fontSize: 13, marginTop: 8, marginBottom: 0 }}>
          {loaded ? 'No label checks yet.' : 'Loading…'}
        </p>
      ) : (
        <table style={{ marginTop: 10 }}>
          <thead>
            <tr>
              <th>Time</th>
              <th>Product</th>
              <th>Result</th>
              <th style={{ textAlign: 'right' }}>Alignment</th>
              <th>Reason</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="muted" style={{ fontSize: 12 }}>
                  {new Date(r.time).toLocaleString()}
                </td>
                <td>{r.productName}</td>
                <td>
                  <Verdict label={r.verdict} inline />
                </td>
                <td className="muted" style={{ fontSize: 12, textAlign: 'right' }}>
                  {r.aligned ? `${Math.round(r.alignScore * 100)}%` : 'not aligned'}
                </td>
                <td className="muted" style={{ fontSize: 12 }}>
                  {r.reason ?? '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

interface LabelProduct {
  id: number;
  name: string;
  referenceName: string;
}
interface LabelRunResult {
  id: string;
  imageKey: string;
  overlay_key: string | null;
  verdict: 'GOOD' | 'BAD' | 'UNSURE';
  aligned: boolean;
  align_score: number;
  reason: string;
}

/**
 * Run a label check, from the portal — the tenant's own "operate" action.
 *
 * Shown only when the account holds `labels` at the operate level (the caller
 * gates this). Pick one of your products that has a reference, upload a photo,
 * get the verdict back. The upload posts multipart to /api/portal/label-inspect,
 * which only ever runs against a product the session owns — the server proves it
 * both before and after storing, so this form cannot reach another customer's.
 */
function LabelRunner({ onDone }: { onDone: () => void }) {
  const [products, setProducts] = useState<LabelProduct[]>([]);
  const [productId, setProductId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<LabelRunResult | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetch('/api/portal/label-products')
      .then((r) => (r.ok ? r.json() : []))
      .then((d: LabelProduct[]) => {
        setProducts(d);
        if (d.length === 1 && d[0]) setProductId(d[0].id);
      })
      .catch(() => setProducts([]));
  }, []);

  const run = async () => {
    const file = fileRef.current?.files?.[0];
    if (!productId) return setError('Choose a product first.');
    if (!file) return setError('Choose a photo of the label first.');
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('productId', String(productId));
      fd.append('image', file);
      const res = await fetch('/api/portal/label-inspect', { method: 'POST', body: fd });
      if (!res.ok) {
        const b = await res.json().catch(() => ({ message: `Failed (${res.status})` }));
        throw new Error(b.message || `Failed (${res.status})`);
      }
      setResult(await res.json());
      if (fileRef.current) fileRef.current.value = '';
      onDone();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (products.length === 0) {
    return (
      <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
        No products are set up for label checks yet — contact us to add a reference label.
      </p>
    );
  }

  return (
    <div
      style={{
        marginTop: 10,
        marginBottom: 6,
        padding: 12,
        border: '1px solid var(--border)',
        borderRadius: 8,
      }}
    >
      <div className="row" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ minWidth: 180 }}>
          <label>Product</label>
          <select
            value={productId ?? ''}
            onChange={(e) => setProductId(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">— choose —</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label>Label photo</label>
          <FileButton inputRef={fileRef} accept="image/*" label="Choose photo" />
        </div>
        <button className="btn primary" disabled={busy} onClick={() => void run()}>
          {busy ? 'Checking…' : 'Run check'}
        </button>
      </div>
      {error && <div style={{ color: 'var(--bad)', fontSize: 13, marginTop: 8 }}>{error}</div>}
      {result && (
        <div className="row" style={{ gap: 16, marginTop: 12, alignItems: 'flex-start' }}>
          <img
            src={`/api/portal/image/${result.overlay_key ?? result.imageKey}`}
            alt="Checked label"
            style={{ width: 200, borderRadius: 6, border: '1px solid var(--border)' }}
          />
          <div>
            <Verdict label={result.verdict} />
            <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
              {result.aligned
                ? `aligned ${Math.round(result.align_score * 100)}%`
                : 'could not align to the reference'}
            </div>
            {result.reason && (
              <div style={{ fontSize: 13, marginTop: 4 }}>{result.reason}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

interface SafetySummary {
  window: string;
  cameras: number;
  events: { total: number; critical: number; warning: number; info: number };
  byKind: Array<{ kind: string; count: number }>;
}
interface SafetyEvent {
  id: string;
  time: string;
  kind: string;
  severity: 'info' | 'warning' | 'critical';
  detail: string | null;
  imageKey: string | null;
  acknowledged: boolean;
  cameraName: string | null;
  zoneName: string | null;
}

const SEVERITY_BADGE: Record<string, string> = { info: '', warning: 'unsure', critical: 'bad' };
const KIND_LABEL: Record<string, string> = {
  ppe_violation: 'PPE missing',
  fire: 'Fire / smoke',
  intrusion: 'No-entry',
  occupancy: 'Over capacity',
  near_miss: 'Near miss',
  person: 'Person',
};

interface PortalCamera {
  id: number;
  name: string;
  location: string | null;
  active: boolean;
  source: string;
  zoneCount: number;
}

/** Which capture path a camera's source implies. */
function cameraMode(source: string): 'usb' | 'ip' | 'upload' {
  if (source === 'usb') return 'usb';
  if (/^https?:\/\//i.test(source)) return 'ip';
  return 'upload';
}

/**
 * The tenant's own safety cameras, set up by them — the self-service half.
 *
 * A camera is Upload, a USB/direct device, or an IP URL. Where a frame comes from
 * is decided here in the browser (a USB device is grabbed, an IP snapshot is
 * fetched, a file is uploaded); the frame is posted to the server, which runs the
 * same detection the staff path does and files the event against this camera.
 */
/** The tenant's own safety API — RLS-scoped, its own cameras only. */
const PORTAL_SAFETY_API = '/api/portal/safety';

/**
 * The tenant's own safety cameras — the self-service half, now built from the
 * SAME shared components the staff use (CameraPicker + CameraConnection), so the
 * fields a client fills in to add a camera are identical to the admin's. The
 * server scopes every call to this tenant, so this only ever reaches their own
 * cameras — never another tenant's, never the staff "Garage".
 */
function PortalSafetyCameras({ onDetected }: { onDetected: () => void }) {
  const [cameras, setCameras] = useState<PortalCamera[]>([]);
  const [selected, setSelected] = useState<number | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The frozen live frame the zone editor draws on; "Grab fresh frame" re-grabs it.
  const [zonesTs, setZonesTs] = useState(() => Date.now());

  const load = useCallback(() => {
    fetch(`${PORTAL_SAFETY_API}/cameras`)
      .then((r) => (r.ok ? r.json() : []))
      .then((cs: PortalCamera[]) => {
        setCameras(cs);
        setSelected((s) => s ?? cs[0]?.id ?? null);
      })
      .catch(() => setCameras([]));
  }, []);
  useEffect(() => load(), [load]);

  const addCamera = async () => {
    try {
      const id = await addSafetyCamera(PORTAL_SAFETY_API);
      if (id == null) return;
      setSelected(id);
      load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const remove = async (id: number) => {
    if (!window.confirm('Remove this camera?')) return;
    await fetch(`${PORTAL_SAFETY_API}/cameras/${id}`, { method: 'DELETE' }).catch(() => undefined);
    setSelected(null);
    load();
  };

  const camera = cameras.find((c) => c.id === selected) ?? null;

  return (
    <div style={{ margin: '10px 0 4px', padding: 12, border: '1px solid var(--border)', borderRadius: 8 }}>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <strong style={{ fontSize: 14 }}>Your cameras</strong>
        <CameraPicker
          cameras={cameras}
          value={selected}
          onChange={setSelected}
          onAdd={() => void addCamera()}
          placeholder={cameras.length ? undefined : 'No cameras yet'}
          style={{ minWidth: 220 }}
        />
      </div>
      {notice && <div style={{ color: 'var(--good)', fontSize: 12, marginTop: 6 }}>{notice}</div>}
      {error && <div style={{ color: 'var(--bad)', fontSize: 13, marginTop: 6 }}>{error}</div>}

      {camera && (
        <div className="grid" style={{ gap: 12, marginTop: 12 }}>
          {isLiveSource(camera.source) && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>
                Live{' '}
                <span className="muted" style={{ fontWeight: 400 }}>— your camera with its zones drawn on the feed</span>
              </div>
              <LiveZonesView cameraId={camera.id} apiBase={PORTAL_SAFETY_API} />
            </div>
          )}
          <div style={{ padding: 10, border: '1px solid var(--border)', borderRadius: 6 }}>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>
              Connect{' '}
              <span className="muted" style={{ fontWeight: 400 }}>
                — its IP &amp; login, then auto-detect the stream and grab a still
              </span>
            </div>
            <CameraConnection camera={camera} apiBase={PORTAL_SAFETY_API} onChanged={load} onNotice={setNotice} onError={setError} />
          </div>

          {isLiveSource(camera.source) && (
            <div style={{ padding: 10, border: '1px solid var(--border)', borderRadius: 6 }}>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>
                Zones{' '}
                <span className="muted" style={{ fontWeight: 400 }}>
                  — outline no-entry / PPE areas on the live view; an alarm fires when a rule is broken
                </span>
              </div>
              <ZoneEditor
                cameraId={camera.id}
                imageUrl={snapshotUrl(camera.id, zonesTs, PORTAL_SAFETY_API)}
                apiBase={PORTAL_SAFETY_API}
                onSaved={(n) => {
                  setNotice(`${n} zone${n === 1 ? '' : 's'} saved.`);
                  load();
                }}
                onError={setError}
                replace={{ label: 'Grab fresh frame', onClick: () => setZonesTs(Date.now()) }}
              />
            </div>
          )}

          <CameraCapture camera={camera} onDetected={onDetected} onRemove={() => void remove(camera.id)} />
        </div>
      )}
    </div>
  );
}

/** Capture a frame from one camera and check it — the path depends on its source. */
function CameraCapture({
  camera,
  onDetected,
  onRemove,
}: {
  camera: PortalCamera;
  onDetected: () => void;
  onRemove: () => void;
}) {
  const mode = cameraMode(camera.source);
  const videoRef = useRef<HTMLVideoElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [deviceId, setDeviceId] = useState('');
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState<{ overlayKey: string | null; events: unknown[] } | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);
  useEffect(() => stop, [stop]);

  const startUsb = async (id?: string) => {
    setNote(null);
    stop();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: (id ?? deviceId) ? { deviceId: { exact: id ?? deviceId } } : true,
      });
      streamRef.current = stream;
      setOn(true);
      const cams = (await navigator.mediaDevices.enumerateDevices()).filter((d) => d.kind === 'videoinput');
      setDevices(cams);
      setDeviceId((cur) => cur || cams[0]?.deviceId || '');
    } catch (e) {
      setNote((e as Error).message);
    }
  };
  useEffect(() => {
    const v = videoRef.current;
    if (on && v && streamRef.current) {
      v.srcObject = streamRef.current;
      void v.play().catch(() => undefined);
    }
  }, [on]);

  const send = async (blob: Blob) => {
    setBusy(true);
    setNote(null);
    try {
      const fd = new FormData();
      fd.append('cameraId', String(camera.id));
      fd.append('image', blob, 'frame.jpg');
      const res = await fetch('/api/portal/safety/detect', { method: 'POST', body: fd });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || `Check failed (${res.status})`);
      const r = await res.json();
      setLast({ overlayKey: r.overlayKey, events: r.events ?? [] });
      onDetected();
    } catch (e) {
      setNote((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const grabFrom = (el: HTMLVideoElement | HTMLImageElement, w: number, h: number) => {
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    try {
      c.getContext('2d')?.drawImage(el, 0, 0, w, h);
      c.toBlob((b) => b && void send(b), 'image/jpeg', 0.92);
    } catch {
      setNote('This camera blocks direct capture (CORS). Enable CORS on the camera, or upload a frame instead.');
    }
  };

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 10 }}>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <strong style={{ fontSize: 13 }}>
          {camera.name}
          <span className="muted" style={{ fontSize: 11 }}>
            {' '}· {mode === 'usb' ? 'connected' : mode === 'ip' ? 'IP' : 'upload'}
            {camera.location ? ` · ${camera.location}` : ''}
          </span>
        </strong>
        <button className="btn sm" onClick={onRemove}>Remove</button>
      </div>

      {mode === 'usb' && (
        <div style={{ marginTop: 8 }}>
          {devices.length > 0 && on && (
            <select value={deviceId} onChange={(e) => void startUsb(e.target.value)} style={{ marginBottom: 8 }}>
              {devices.map((d, i) => (
                <option key={d.deviceId || i} value={d.deviceId}>{d.label || `Camera ${i + 1}`}</option>
              ))}
            </select>
          )}
          {on ? (
            <>
              <video ref={videoRef} autoPlay playsInline muted style={{ width: '100%', borderRadius: 6, background: '#000' }} />
              <div className="row" style={{ marginTop: 8, gap: 8 }}>
                <button className="btn sm primary" disabled={busy} onClick={() => { const v = videoRef.current; if (v?.videoWidth) grabFrom(v, v.videoWidth, v.videoHeight); }}>
                  {busy ? 'Checking…' : 'Capture & check'}
                </button>
                <button className="btn sm" onClick={() => { stop(); setOn(false); }}>Stop</button>
              </div>
            </>
          ) : (
            <button className="btn sm" onClick={() => void startUsb()}>Enable camera</button>
          )}
        </div>
      )}

      {mode === 'ip' && (
        <div style={{ marginTop: 8 }}>
          <img ref={imgRef} crossOrigin="anonymous" src={camera.source} alt={camera.name} style={{ width: '100%', borderRadius: 6, background: '#000' }} onError={() => setNote('Could not load the camera stream — check the URL and that this PC can reach it.')} />
          <button className="btn sm primary" style={{ marginTop: 8 }} disabled={busy} onClick={() => { const im = imgRef.current; if (im?.naturalWidth) grabFrom(im, im.naturalWidth, im.naturalHeight); }}>
            {busy ? 'Checking…' : 'Capture & check'}
          </button>
        </div>
      )}

      {mode === 'upload' && (
        <div className="row" style={{ marginTop: 8, gap: 8, alignItems: 'center' }}>
          <FileButton inputRef={fileRef} accept="image/*" label="Choose photo" />
          <button className="btn sm primary" disabled={busy} onClick={() => { const f = fileRef.current?.files?.[0]; if (f) void send(f); }}>
            {busy ? 'Checking…' : 'Check'}
          </button>
        </div>
      )}

      {note && <div className="muted" style={{ fontSize: 12, marginTop: 6, color: 'var(--bad)' }}>{note}</div>}
      {last && (
        <div style={{ marginTop: 8 }}>
          {last.overlayKey && (
            <img src={`/api/portal/image/${last.overlayKey}`} alt="Checked frame" style={{ width: '100%', borderRadius: 6 }} />
          )}
          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
            {last.events.length === 0 ? 'No safety issues in this frame.' : `${last.events.length} event(s) raised.`}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Workplace safety, in the portal — the tenant's own cameras and what they
 * raised. Read-only for a view-only tenant; an operate-level one also sets up
 * cameras and runs checks. Events and cameras are scoped through the camera
 * under row-level security, so a customer sees exactly their own facility.
 */
function PortalSafety() {
  const { user } = useAuth();
  const canOperateSafety = canOperate(user?.features ?? null, 'safety');
  const [summary, setSummary] = useState<SafetySummary | null>(null);
  const [events, setEvents] = useState<SafetyEvent[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [wizard, setWizard] = useState(false);
  const [cams, setCams] = useState<Array<{ id: number; name: string }>>([]);
  useEffect(() => {
    fetch('/api/portal/safety/cameras').then((r) => (r.ok ? r.json() : [])).then(setCams).catch(() => undefined);
  }, []);

  const reload = useCallback(() => {
    fetch('/api/portal/safety/summary?window=7d')
      .then((r) => (r.ok ? r.json() : null))
      .then(setSummary)
      .catch(() => undefined);
    fetch('/api/portal/safety/events?limit=12')
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => {
        setEvents(d);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);
  const canAck = canOperateSafety;

  const ack = async (id: string) => {
    try {
      const res = await fetch(`/api/portal/safety/events/${id}/ack`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ acknowledged: true }),
      });
      if (res.ok) setEvents((es) => es.map((e) => (e.id === id ? { ...e, acknowledged: true } : e)));
    } catch {
      /* leave the event as-is; the operator can retry */
    }
  };

  const tile = (label: string, value: number, tone?: string) => (
    <div style={{ minWidth: 90 }}>
      <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>
        {label}
      </div>
      <div
        style={{
          fontSize: 22,
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
          color: tone,
        }}
      >
        {value}
      </div>
    </div>
  );

  return (
    <>
    <div className="panel" style={{ marginTop: 20 }}>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
        <strong>Workplace safety</strong>
        {canOperateSafety && <button className="btn sm primary" onClick={() => setWizard(true)}>+ New scenario</button>}
      </div>
      {wizard && (
        <SafetyScenarioWizard
          apiBase="/api/portal/safety"
          portal
          onClose={() => setWizard(false)}
          onDone={() => { setWizard(false); reload(); }}
        />
      )}
      {canOperateSafety && <PortalSafetyCameras onDetected={reload} />}
      {summary && (
        <div className="row" style={{ gap: 24, flexWrap: 'wrap', marginTop: 12 }}>
          {tile('Cameras', summary.cameras)}
          {tile('Events · 7d', summary.events.total)}
          {tile('Critical', summary.events.critical, summary.events.critical ? 'var(--bad)' : undefined)}
          {tile('Warnings', summary.events.warning, summary.events.warning ? 'var(--warn)' : undefined)}
        </div>
      )}
      {events.length === 0 ? (
        <p className="muted" style={{ fontSize: 13, marginTop: 10, marginBottom: 0 }}>
          {loaded ? 'No safety events in this period — nothing to report.' : 'Loading…'}
        </p>
      ) : (
        <table style={{ marginTop: 12 }}>
          <thead>
            <tr>
              <th />
              <th>Time</th>
              <th>Event</th>
              <th>Where</th>
              <th style={{ textAlign: 'right' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {events.map((e) => (
              <tr key={e.id}>
                <td style={{ width: 64 }}>
                  {e.imageKey && (
                    <img
                      src={`/api/portal/image/${e.imageKey}`}
                      alt=""
                      style={{ width: 56, height: 40, objectFit: 'cover', borderRadius: 4, display: 'block' }}
                    />
                  )}
                </td>
                <td className="muted" style={{ fontSize: 12 }}>
                  {new Date(e.time).toLocaleString()}
                </td>
                <td>
                  <span className={`badge ${SEVERITY_BADGE[e.severity] || ''}`}>
                    {KIND_LABEL[e.kind] ?? e.kind}
                  </span>
                  {e.detail && (
                    <span className="muted" style={{ fontSize: 12, marginLeft: 6 }}>
                      {e.detail}
                    </span>
                  )}
                </td>
                <td className="muted" style={{ fontSize: 12 }}>
                  {[e.cameraName, e.zoneName].filter(Boolean).join(' · ') || '—'}
                </td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {e.acknowledged ? (
                    <span className="muted" style={{ fontSize: 12 }}>✓ acknowledged</span>
                  ) : canAck ? (
                    <button className="btn sm" onClick={() => void ack(e.id)}>
                      Acknowledge
                    </button>
                  ) : (
                    <span className="muted" style={{ fontSize: 12 }}>open</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
    <SafetySiteLeaderboard apiBase="/api/portal/safety" />
    <SafetyActivityPanel apiBase="/api/portal/safety" cameras={cams} />
    </>
  );
}

/**
 * The line over time — the thing a customer looks for first and the demo pain
 * when it is missing ("I don't see any trend lines").
 *
 * Stacked columns: GOOD at the base, UNSURE, then BAD on top. Height is
 * throughput and the split is quality, so one chart answers both "how much ran"
 * and "how did it go" on a single count axis — never a second y-axis, which
 * would be comparing two scales that share no unit.
 *
 * Quality inspection only. Classification has no verdict and belongs to a
 * different measure; the backend counts only anomaly rows, and this says so, so
 * nobody reads a blended number that moves with the mix of work.
 *
 * The colours are the reserved status set — green / amber / red — validated for
 * colourblind separation (worst adjacent pair ΔE 11.3, above the floor). They
 * are never colour alone: a fixed stacking order, a 2px gap between segments,
 * and a labelled legend all carry the same meaning a second way.
 */
function TrendChart({ trend }: { trend: PortalTrend }) {
  const [hover, setHover] = useState<number | null>(null);

  const C = { good: '#0ca30c', unsure: '#fab219', bad: '#d03b3b' };
  const W = 720;
  const H = 220;
  const padL = 40;
  const padR = 12;
  const padT = 14;
  const padB = 34;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;

  const buckets = trend.buckets;
  const max = Math.max(1, ...buckets.map((b) => b.good + b.bad + b.unsure));
  // A "nice" top tick so the axis reads in round numbers, not 1..847.
  const niceMax = niceCeil(max);
  const y = (v: number) => padT + plotH - (v / niceMax) * plotH;
  const bandW = plotW / buckets.length;
  const barW = Math.max(2, Math.min(bandW - 3, 26));

  const ticks = [0, niceMax / 2, niceMax];
  const fmtDate = (iso: string) =>
    trend.bucket === 'hour'
      ? new Date(iso).toLocaleTimeString([], { hour: '2-digit' })
      : new Date(iso).toLocaleDateString([], { day: '2-digit', month: '2-digit' });

  // Label every column would collide; show a handful of evenly-spaced ones.
  const labelEvery = Math.ceil(buckets.length / 8);

  return (
    <div className="panel" style={{ marginBottom: 20 }}>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <strong>Quality inspection over time</strong>
        <div className="row" style={{ gap: 14, fontSize: 12 }}>
          {(['good', 'unsure', 'bad'] as const).map((k) => (
            <span key={k} className="row" style={{ gap: 5 }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: C[k] }} />
              <span className="muted" style={{ textTransform: 'capitalize' }}>
                {k === 'good' ? 'Good' : k === 'unsure' ? 'Unsure' : 'Rejected'}
              </span>
            </span>
          ))}
        </div>
      </div>

      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        role="img"
        aria-label="Quality inspection verdicts over time"
        style={{ marginTop: 10, display: 'block', overflow: 'visible' }}
        onMouseLeave={() => setHover(null)}
      >
        {/* Recessive gridlines + count axis */}
        {ticks.map((t) => (
          <g key={t}>
            <line x1={padL} x2={W - padR} y1={y(t)} y2={y(t)} stroke="var(--border)" strokeWidth={1} />
            <text x={padL - 6} y={y(t) + 4} textAnchor="end" fontSize={11} fill="var(--text-dim)">
              {Math.round(t)}
            </text>
          </g>
        ))}

        {buckets.map((b, i) => {
          const total = b.good + b.bad + b.unsure;
          const cx = padL + i * bandW + (bandW - barW) / 2;
          // Stack order: good at the base, then unsure, then bad on top.
          const segs = [
            { k: 'good', v: b.good, c: C.good },
            { k: 'unsure', v: b.unsure, c: C.unsure },
            { k: 'bad', v: b.bad, c: C.bad },
          ].filter((s) => s.v > 0);
          let yTop = y(0);
          return (
            <g key={b.t}>
              {segs.map((s, si) => {
                const h = (s.v / niceMax) * plotH;
                yTop -= h;
                // 2px surface gap between segments, so adjacent colours read as
                // two marks rather than one blended band.
                const gap = si > 0 ? 2 : 0;
                return (
                  <rect
                    key={s.k}
                    x={cx}
                    y={yTop + gap}
                    width={barW}
                    height={Math.max(0, h - gap)}
                    fill={s.c}
                    opacity={hover === null || hover === i ? 1 : 0.45}
                    rx={2}
                  />
                );
              })}
              {/* Full-height hit target, wider than the bar. */}
              <rect
                x={padL + i * bandW}
                y={padT}
                width={bandW}
                height={plotH}
                fill="transparent"
                onMouseEnter={() => setHover(i)}
              />
              {i % labelEvery === 0 && (
                <text
                  x={cx + barW / 2}
                  y={H - padB + 16}
                  textAnchor="middle"
                  fontSize={10}
                  fill="var(--text-dim)"
                >
                  {fmtDate(b.t)}
                </text>
              )}
              {hover === i && total > 0 && (
                <TrendTooltip x={cx + barW / 2} plotW={plotW} padL={padL} bucket={b} date={fmtDate(b.t)} />
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function TrendTooltip({
  x,
  plotW,
  padL,
  bucket,
  date,
}: {
  x: number;
  plotW: number;
  padL: number;
  bucket: { good: number; bad: number; unsure: number };
  date: string;
}) {
  const total = bucket.good + bucket.bad + bucket.unsure;
  const pass = total > 0 ? ((bucket.good / total) * 100).toFixed(1) : '—';
  const w = 150;
  const h = 78;
  // Keep the box inside the plot rather than letting it run off the right edge.
  const left = Math.min(Math.max(x - w / 2, padL), padL + plotW - w);
  const rows: Array<[string, number, string]> = [
    ['Good', bucket.good, '#0ca30c'],
    ['Unsure', bucket.unsure, '#fab219'],
    ['Rejected', bucket.bad, '#d03b3b'],
  ];
  return (
    <g pointerEvents="none">
      <rect x={left} y={4} width={w} height={h} rx={6} fill="#0d1117" stroke="var(--border)" />
      <text x={left + 10} y={20} fontSize={11} fill="var(--text-dim)">
        {date} · {pass}% pass
      </text>
      {rows.map(([label, v, c], i) => (
        <g key={label} transform={`translate(${left + 10}, ${34 + i * 14})`}>
          <rect x={0} y={-8} width={9} height={9} rx={2} fill={c} />
          <text x={15} y={0} fontSize={11} fill="var(--text)">
            {label}
          </text>
          <text x={w - 20} y={0} fontSize={11} fill="var(--text)" textAnchor="end" fontFamily="var(--mono)">
            {v}
          </text>
        </g>
      ))}
    </g>
  );
}

/** Round a max up to a clean axis top (1/2/5 × 10ⁿ). */
function niceCeil(v: number): number {
  if (v <= 5) return 5;
  const mag = Math.pow(10, Math.floor(Math.log10(v)));
  const n = v / mag;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return step * mag;
}

function ProductPanel({ product }: { product: PortalProduct }) {
  const a = product.anomaly;
  const c = product.classification;
  const passRate = a && product.total > 0 ? (a.good / product.total) * 100 : null;

  return (
    <div className="panel">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <div>
          <strong style={{ fontSize: 18 }}>{product.name}</strong>
          <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>
            {TASKS[product.task].question}
          </div>
        </div>
        <span className="muted" style={{ fontFamily: 'var(--mono)', fontSize: 13 }}>
          {product.total.toLocaleString()} inspected
        </span>
      </div>

      {product.total === 0 ? (
        <div className="muted" style={{ fontSize: 13, marginTop: 10 }}>
          Nothing ran in this window.
        </div>
      ) : (
        <div className="row" style={{ gap: 16, marginTop: 14, alignItems: 'stretch' }}>
          {a && (
            <>
              <Tile
                label="Pass rate"
                value={passRate === null ? '—' : `${passRate.toFixed(1)}%`}
                tone="var(--good)"
              />
              <Tile label="Rejected" value={a.bad.toLocaleString()} tone="var(--bad)" />
              <Tile label="Unsure" value={a.unsure.toLocaleString()} tone="var(--warn)" />
            </>
          )}
          {c && (
            <>
              <Tile label="Classified" value={c.classified.toLocaleString()} />
              <Tile label="Sent to a person" value={c.rejected.toLocaleString()} tone="var(--warn)" />
              <Tile label="Corrected" value={c.corrected.toLocaleString()} />
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Tile({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div className="stat-tile" style={{ flex: '1 1 150px' }}>
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={tone ? { color: tone } : undefined}>
        {value}
      </div>
    </div>
  );
}

/**
 * A verdict in its own colour.
 *
 * The status palette, used for status — the one thing it is reserved for.
 */
function Verdict({ label, inline }: { label: string; inline?: boolean }) {
  const tone =
    label === 'GOOD' ? 'var(--good)' : label === 'BAD' ? 'var(--bad)' : 'var(--warn)';
  return (
    <span
      style={{
        color: tone,
        fontWeight: 700,
        fontSize: inline ? 10 : 12,
        display: inline ? 'inline' : 'block',
        marginTop: inline ? 0 : 2,
      }}
    >
      {label}
    </span>
  );
}

/** Let a customer replace the password somebody else chose for them. */
function ChangePassword({ onDone }: { onDone: (message: string) => void }) {
  const [currentPassword, setCurrent] = useState('');
  const [password, setNext] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      const res = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        // `newPassword`, not `password` — see ChangePasswordSchema. Sending
        // the wrong field name failed validation and the form showed nothing,
        // because the error path was a field the form did not have.
        body: JSON.stringify({ currentPassword, newPassword: password }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(body.message ?? 'Could not change the password');
      }
      onDone('Password changed.');
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="panel" style={{ marginBottom: 16 }}>
      <strong>Change your password</strong>
      <div className="row" style={{ gap: 12, marginTop: 12, alignItems: 'flex-end' }}>
        <div style={{ flex: 1, minWidth: 180 }}>
          <label>Current password</label>
          <input
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrent(e.target.value)}
            style={{ width: '100%' }}
          />
        </div>
        <div style={{ flex: 1, minWidth: 180 }}>
          <label>New password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setNext(e.target.value)}
            style={{ width: '100%' }}
          />
        </div>
        <button
          className="btn primary"
          disabled={busy || password.length < 10}
          onClick={() => void submit()}
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
      <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
        At least 10 characters — length beats symbols.
      </div>
      {err && <div style={{ color: 'var(--bad)', fontSize: 13, marginTop: 8 }}>{err}</div>}
    </div>
  );
}

interface AgentKeyRow {
  id: number;
  name: string;
  scope: 'read' | 'operate';
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
}

interface AgentUsage {
  window: string;
  total: number;
  byKey: Array<{ id: number; name: string; calls: number; lastUsedAt: string | null }>;
}

/**
 * BYOA — Bring Your Own Agent.
 *
 * The badge is a claim about the product, so it heads the section rather than
 * sitting in a corner. Below it is the machinery that makes the claim true: a
 * key the customer's own agent authenticates with, the endpoint it connects to,
 * and the meter that says what those agents did — because an agent call is
 * billable and the customer should see the number before the invoice does.
 *
 * Only the main contact can mint or revoke a key (the backend enforces it too);
 * everyone else sees the keys and usage but is pointed at the main contact. A
 * raw key is shown exactly once, at creation, and never recoverable after.
 */
function PortalAgents() {
  const { user } = useAuth();
  const [keys, setKeys] = useState<AgentKeyRow[] | null>(null);
  const [usage, setUsage] = useState<AgentUsage | null>(null);
  const [isPrimary, setIsPrimary] = useState(false);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [scope, setScope] = useState<'read' | 'operate'>('read');
  const [fresh, setFresh] = useState<{ name: string; key: string } | null>(null);
  const [showGuide, setShowGuide] = useState(false);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const endpoint = `${window.location.origin}/api/portal/mcp`;

  const loadKeys = useCallback(() => {
    fetch('/api/portal/agent-keys')
      .then((r) => (r.ok ? r.json() : { keys: [] }))
      .then((d) => setKeys(d.keys ?? []))
      .catch(() => setKeys([]));
  }, []);
  useEffect(() => loadKeys(), [loadKeys]);
  useEffect(() => {
    fetch('/api/portal/agent-keys/usage?window=30d')
      .then((r) => (r.ok ? r.json() : null))
      .then(setUsage)
      .catch(() => setUsage(null));
  }, [keys]);
  // Am I the main contact? The create form only shows for them.
  useEffect(() => {
    fetch('/api/portal/colleagues')
      .then((r) => (r.ok ? r.json() : []))
      .then((rows: Colleague[]) => setIsPrimary(rows.find((r) => r.isPrimary)?.id === user?.id))
      .catch(() => setIsPrimary(false));
  }, [user?.id]);

  async function create() {
    setBusy(true);
    setErr(null);
    try {
      const res = await fetch('/api/portal/agent-keys', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), scope }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d?.message ?? 'Could not create the key');
      setFresh({ name: d.name, key: d.key });
      setCreating(false);
      setName('');
      setScope('read');
      loadKeys();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id: number) {
    setErr(null);
    const res = await fetch(`/api/portal/agent-keys/${id}/revoke`, { method: 'POST' });
    if (res.ok) loadKeys();
    else setErr('Could not revoke the key');
  }

  const active = keys?.filter((k) => !k.revokedAt) ?? [];

  return (
    <div id="byoa" className="panel" style={{ marginTop: 20, scrollMarginTop: 16 }}>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <img
          src="/byoa-badge.svg"
          alt="BYOA Ready — Bring Your Own Agent"
          style={{ height: 52, display: 'block' }}
        />
        {isPrimary && !fresh && (
          <button className="btn sm" onClick={() => { setCreating((c) => !c); setErr(null); }}>
            {creating ? 'Cancel' : '+ New agent key'}
          </button>
        )}
      </div>

      <div className="muted" style={{ fontSize: 13, marginTop: 10, maxWidth: 620 }}>
        Connect your own AI agent to your account. Point an MCP-capable agent at the endpoint below
        and authenticate with an agent key. The agent sees only your data, at the level the key
        allows, and every call is metered.
      </div>

      <div style={{ marginTop: 12 }}>
        <label style={{ fontSize: 12 }}>MCP endpoint</label>
        <div
          style={{
            fontFamily: 'var(--mono)',
            fontSize: 13,
            padding: '8px 10px',
            borderRadius: 6,
            background: 'var(--panel-2, rgba(127,127,127,0.08))',
            wordBreak: 'break-all',
          }}
        >
          {endpoint}
        </div>
        <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
          Send the key as <code>Authorization: Bearer …</code> or an <code>x-agent-key</code> header.
        </div>
      </div>

      {/* Full step-by-step setup guide, collapsed by default. */}
      <div style={{ marginTop: 12 }}>
        <button className="btn sm" onClick={() => setShowGuide((g) => !g)}>
          {showGuide ? '▾ Hide setup guide' : '▸ Read the setup guide'}
        </button>
        {showGuide && (
          <div
            style={{
              marginTop: 12,
              paddingTop: 4,
              borderTop: '1px solid var(--border, rgba(127,127,127,0.2))',
            }}
          >
            <ByoaGuide endpoint={endpoint} />
          </div>
        )}
      </div>

      {err && <div style={{ color: 'var(--bad)', fontSize: 13, marginTop: 10 }}>{err}</div>}

      {/* The key, shown once. */}
      {fresh && (
        <div
          className="panel"
          style={{ marginTop: 14, border: '1px solid var(--good)', background: 'transparent' }}
        >
          <strong>New key “{fresh.name}”</strong>
          <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
            Copy it now — it is shown only this once and cannot be recovered later.
          </div>
          <div className="row" style={{ gap: 8, marginTop: 8, alignItems: 'center' }}>
            <code
              style={{
                fontSize: 13,
                padding: '8px 10px',
                borderRadius: 6,
                background: 'var(--panel-2, rgba(127,127,127,0.08))',
                wordBreak: 'break-all',
                flex: 1,
              }}
            >
              {fresh.key}
            </code>
            <button
              className="btn sm"
              onClick={() => {
                void navigator.clipboard?.writeText(fresh.key);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              }}
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <button className="btn sm" style={{ marginTop: 10 }} onClick={() => setFresh(null)}>
            Done
          </button>
        </div>
      )}

      {creating && (
        <div className="panel" style={{ marginTop: 14, background: 'transparent' }}>
          <div className="row" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <label style={{ fontSize: 12 }}>Name</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Ops copilot"
                style={{ minWidth: 200 }}
              />
            </div>
            <div>
              <label style={{ fontSize: 12 }}>Access</label>
              <select value={scope} onChange={(e) => setScope(e.target.value as 'read' | 'operate')}>
                <option value="read">Read only</option>
                <option value="operate">Read &amp; operate</option>
              </select>
            </div>
            <button
              className="btn sm primary"
              disabled={busy || name.trim().length < 2}
              onClick={() => void create()}
            >
              Create key
            </button>
          </div>
          <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
            A read key can never operate a device — it is capped at view on every function. Grant
            operate only to an agent you want to act on your line.
          </div>
        </div>
      )}

      {/* Keys */}
      {keys && active.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ marginTop: 14, width: '100%', fontVariantNumeric: 'tabular-nums' }}>
            <thead>
              <tr>
                <th>Key</th><th>Access</th><th>Created</th><th>Last used</th>
                {isPrimary && <th></th>}
              </tr>
            </thead>
            <tbody>
              {active.map((k) => (
                <tr key={k.id}>
                  <td>{k.name}</td>
                  <td>
                    <span
                      style={{
                        fontSize: 11,
                        padding: '2px 8px',
                        borderRadius: 999,
                        border: '1px solid var(--border, rgba(127,127,127,0.3))',
                        color: k.scope === 'operate' ? 'var(--warn)' : 'var(--muted)',
                      }}
                    >
                      {k.scope === 'operate' ? 'read & operate' : 'read only'}
                    </span>
                  </td>
                  <td className="muted" style={{ fontSize: 13 }}>
                    {new Date(k.createdAt).toLocaleDateString()}
                  </td>
                  <td className="muted" style={{ fontSize: 13 }}>
                    {k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString() : 'never'}
                  </td>
                  {isPrimary && (
                    <td>
                      <button className="btn sm" onClick={() => void revoke(k.id)}>Revoke</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {keys && active.length === 0 && !creating && !fresh && (
        <div className="muted" style={{ fontSize: 13, marginTop: 14 }}>
          {isPrimary
            ? 'No agent keys yet. Create one to connect an agent.'
            : 'No agent keys yet. Ask the main contact for this account to create one.'}
        </div>
      )}

      {/* The billable meter. */}
      {usage && usage.total > 0 && (
        <div style={{ marginTop: 16 }}>
          <strong style={{ fontSize: 14 }}>Agent activity</strong>
          <span className="muted" style={{ fontSize: 12, marginLeft: 8 }}>
            {usage.total.toLocaleString()} calls in the last 30 days
          </span>
          <table style={{ marginTop: 8, width: '100%', fontVariantNumeric: 'tabular-nums' }}>
            <tbody>
              {usage.byKey
                .filter((k) => k.calls > 0)
                .map((k) => (
                  <tr key={k.id}>
                    <td>{k.name}</td>
                    <td style={{ textAlign: 'right' }}>{k.calls.toLocaleString()} calls</td>
                    <td className="muted" style={{ fontSize: 12, textAlign: 'right' }}>
                      {k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString() : ''}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

interface Colleague {
  id: number;
  name: string;
  active: boolean;
  email: string | null;
  jobFunction: string | null;
  isPrimary: boolean;
  hasOwnPassword: boolean;
}

const JOB_FUNCTIONS = [
  'Sales',
  'Finance',
  'Quality',
  'Technology',
  'Operations',
  'Management',
  'Other',
];

/**
 * Who else is in this account.
 *
 * The job function is a LABEL. Everyone here sees this same page — Finance does
 * not get a different one from Quality — and the panel says so, because a field
 * called "function" looks like a permission and somebody will assume it is one.
 */
function Colleagues() {
  const [rows, setRows] = useState<Colleague[]>([]);
  const [adding, setAdding] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(() => {
    fetch('/api/portal/colleagues')
      .then((r) => (r.ok ? r.json() : []))
      .then(setRows)
      .catch(() => setRows([]));
  }, []);
  useEffect(() => load(), [load]);

  const me = rows.find((r) => r.isPrimary);
  const canInvite = Boolean(me);

  return (
    <div className="panel" style={{ marginTop: 20 }}>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <div>
          <strong>People in this account</strong>
          <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
            Everyone here sees this same page. The function is a label, not a permission.
          </div>
        </div>
        {canInvite && (
          <button className="btn sm" onClick={() => setAdding((a) => !a)}>
            {adding ? 'Cancel' : '+ Invite a colleague'}
          </button>
        )}
      </div>

      {err && <div style={{ color: 'var(--bad)', fontSize: 13, marginTop: 8 }}>{err}</div>}

      {adding && (
        <InviteColleague
          onDone={() => {
            setAdding(false);
            load();
          }}
          onError={setErr}
        />
      )}

      <table style={{ marginTop: 12 }}>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} style={{ cursor: 'default', opacity: r.active ? 1 : 0.5 }}>
              <td>
                {r.name}
                {r.isPrimary && (
                  <span className="muted" style={{ fontSize: 11 }}> · main contact</span>
                )}
                {!r.active && <span className="muted" style={{ fontSize: 11 }}> · retired</span>}
              </td>
              <td className="muted" style={{ fontSize: 13 }}>{r.jobFunction ?? '—'}</td>
              <td className="muted" style={{ fontSize: 13 }}>{r.email ?? ''}</td>
              <td style={{ fontSize: 12 }}>
                {/*
                  An invited colleague starts on a password the inviter chose
                  and therefore knows. Saying who has not replaced it yet is the
                  only way that ever gets noticed.
                */}
                {!r.hasOwnPassword && (
                  <span style={{ color: 'var(--warn)' }}>still on the password they were given</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function InviteColleague({
  onDone,
  onError,
}: {
  onDone: () => void;
  onError: (m: string) => void;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [jobFunction, setJobFunction] = useState('Quality');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const res = await fetch('/api/portal/colleagues', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim() || undefined,
          jobFunction,
          password,
        }),
      });
      if (!res.ok) {
        const b = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(b.message ?? 'Could not add this colleague');
      }
      onDone();
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ background: 'var(--bg-panel-2)', borderRadius: 8, padding: 14, marginTop: 12 }}>
      <div className="row" style={{ gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 160px' }}>
          <label>Login name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} style={{ width: '100%' }} />
        </div>
        <div style={{ flex: '1 1 180px' }}>
          <label>Email</label>
          <input value={email} onChange={(e) => setEmail(e.target.value)} style={{ width: '100%' }} />
        </div>
        <div style={{ flex: '0 0 150px' }}>
          <label>Function</label>
          <select
            value={jobFunction}
            onChange={(e) => setJobFunction(e.target.value)}
            style={{ width: '100%' }}
          >
            {JOB_FUNCTIONS.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
        </div>
        <div style={{ flex: '1 1 160px' }}>
          <label>First password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={{ width: '100%' }}
          />
        </div>
        <button
          className="btn primary"
          disabled={busy || name.trim().length < 2 || password.length < 10}
          onClick={() => void submit()}
        >
          {busy ? 'Adding…' : 'Add'}
        </button>
      </div>
      <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
        Give them this password in person or by phone, and ask them to change it once they are in —
        until they do, you can still sign in as them.
      </div>
    </div>
  );
}

// ── Customer-facing QC screens (in proportion to the reference product) ───────

type Win = '24h' | '7d' | '30d';
const QC_WINDOWS: Array<{ key: Win; label: string }> = [
  { key: '24h', label: '24h' },
  { key: '7d', label: '7 days' },
  { key: '30d', label: '30 days' },
];

/** A small 24h / 7d / 30d selector. */
function WindowPicker({ win, onWin }: { win: Win; onWin: (w: Win) => void }) {
  return (
    <div className="row" style={{ gap: 4 }}>
      {QC_WINDOWS.map((w) => (
        <button key={w.key} className={`btn sm ${win === w.key ? 'primary' : ''}`} onClick={() => onWin(w.key)}>
          {w.label}
        </button>
      ))}
    </div>
  );
}

interface DefectRow { id: number; name: string; color: string | null; count: number; pct: number }
interface DefectData { totalDefects: number; categories: number; topDefect: { id: number; name: string } | null; defects: DefectRow[] }

/** Object-detection defect breakdown — which defects the line throws, and how often. */
function PortalQualityAnalytics() {
  const [win, setWin] = useState<Win>('7d');
  const [d, setD] = useState<DefectData | null>(null);
  useEffect(() => {
    fetch(`/api/portal/quality/defects?window=${win}`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setD)
      .catch(() => setD(null));
  }, [win]);
  return (
    <div className="panel" style={{ marginTop: 20 }}>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <strong>Quality analytics — defects</strong>
        <WindowPicker win={win} onWin={setWin} />
      </div>
      {!d ? (
        <div className="muted" style={{ marginTop: 10 }}>Loading…</div>
      ) : d.defects.length === 0 ? (
        <div className="muted" style={{ marginTop: 10 }}>No defects recorded in this period.</div>
      ) : (
        <>
          <div className="row" style={{ gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
            <Tile label="Total defects" value={String(d.totalDefects)} tone="var(--bad)" />
            <Tile label="Categories" value={String(d.categories)} />
            <Tile label="Top defect" value={d.topDefect?.name ?? '—'} tone="var(--accent)" />
          </div>
          <div className="grid" style={{ gap: 8, marginTop: 14 }}>
            {d.defects.map((x) => (
              <div key={x.id} className="row" style={{ gap: 12, alignItems: 'center' }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: x.color || 'var(--bad)', flex: '0 0 auto' }} />
                <span style={{ flex: '0 0 150px', fontSize: 13, fontWeight: 600 }}>{x.name}</span>
                <span style={{ flex: '0 0 60px', fontVariantNumeric: 'tabular-nums', fontSize: 13 }}>{x.count}</span>
                <span className="muted" style={{ flex: '0 0 52px', fontSize: 12 }}>{x.pct}%</span>
                <span style={{ flex: 1, height: 8, background: 'var(--border)', borderRadius: 4, overflow: 'hidden' }}>
                  <span style={{ display: 'block', height: '100%', width: `${x.pct}%`, background: x.color || 'var(--bad)', borderRadius: 4 }} />
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

interface ClassRow { id: number; name: string; count: number; pct: number }
interface ClassData { classified: number; corrected: number; agreementPct: number | null; classes: number; distribution: ClassRow[] }
/**
 * Classification analytics — the class distribution and how often a human
 * overruled the model. Classification has no pass/fail verdict, so this is its
 * own measure rather than the anomaly defect breakdown it used to borrow.
 */
function PortalClassification() {
  const [win, setWin] = useState<Win>('30d');
  const [d, setD] = useState<ClassData | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    fetch(`/api/portal/classification?window=${win}`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setD)
      .catch(() => setD(null))
      .finally(() => setLoading(false));
  }, [win]);
  return (
    <div className="panel" style={{ marginTop: 20 }}>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <strong>Classification analytics</strong>
        <WindowPicker win={win} onWin={setWin} />
      </div>
      {loading ? (
        <div className="muted" style={{ marginTop: 10 }}>Loading…</div>
      ) : !d || d.classified === 0 ? (
        <div className="muted" style={{ marginTop: 10 }}>No classifications recorded in this period.</div>
      ) : (
        <>
          <div className="row" style={{ gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
            <Tile label="Classified" value={String(d.classified)} />
            <Tile label="Human-corrected" value={String(d.corrected)} tone="var(--warn)" />
            <Tile label="Agreement" value={d.agreementPct != null ? `${d.agreementPct}%` : '—'} tone="var(--good)" />
            <Tile label="Classes seen" value={String(d.classes)} />
          </div>
          <div className="grid" style={{ gap: 8, marginTop: 14 }}>
            {d.distribution.map((x) => (
              <div key={x.id} className="row" style={{ gap: 12, alignItems: 'center' }}>
                <span style={{ flex: '0 0 160px', fontSize: 13, fontWeight: 600 }}>{x.name}</span>
                <span style={{ flex: '0 0 60px', fontVariantNumeric: 'tabular-nums', fontSize: 13 }}>{x.count}</span>
                <span className="muted" style={{ flex: '0 0 52px', fontSize: 12 }}>{x.pct}%</span>
                <span style={{ flex: 1, height: 8, background: 'var(--border)', borderRadius: 4, overflow: 'hidden' }}>
                  <span style={{ display: 'block', height: '100%', width: `${x.pct}%`, background: 'var(--accent)', borderRadius: 4 }} />
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

interface SummaryData { total: number; passed: number; requiresReview: number; manualInspect: number }
/** Image-directory headline counts, matching the cards above the image grid. */
function PortalImageDirectory() {
  const [win, setWin] = useState<Win>('7d');
  const [s, setS] = useState<SummaryData | null>(null);
  useEffect(() => {
    fetch(`/api/portal/quality/summary?window=${win}`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setS)
      .catch(() => setS(null));
  }, [win]);
  const passRate = s && s.total ? Math.round((s.passed / s.total) * 100) : 0;
  return (
    <div className="panel" style={{ marginTop: 20 }}>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <strong>Image directory</strong>
        <WindowPicker win={win} onWin={setWin} />
      </div>
      <div className="row" style={{ gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
        <Tile label="Inspected" value={String(s?.total ?? 0)} />
        <Tile label={`Passed · ${passRate}%`} value={String(s?.passed ?? 0)} tone="var(--good)" />
        <Tile label="Requires review" value={String(s?.requiresReview ?? 0)} tone="var(--bad)" />
        <Tile label="Manual inspect" value={String(s?.manualInspect ?? 0)} tone="var(--warn)" />
      </div>
    </div>
  );
}

interface BatchRow { batchId: string; inspected: number; ok: number; ng: number; shift: string | null; startTime: string; endTime: string; minutes: number }
/** Per-batch production records — one row per production run. */
function PortalBatches() {
  const [rows, setRows] = useState<BatchRow[] | null>(null);
  useEffect(() => {
    fetch('/api/portal/quality/batches')
      .then((r) => (r.ok ? r.json() : []))
      .then(setRows)
      .catch(() => setRows([]));
  }, []);
  if (rows && rows.length === 0) return null; // hidden until batches carry a batch_id
  return (
    <div className="panel" style={{ marginTop: 20 }}>
      <strong>Production analytics — batches</strong>
      {!rows ? (
        <div className="muted" style={{ marginTop: 10 }}>Loading…</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ marginTop: 12, width: '100%', fontVariantNumeric: 'tabular-nums' }}>
            <thead>
              <tr>
                <th>Batch</th><th>Inspected</th><th>OK</th><th>NG</th><th>Shift</th><th>Start</th><th>End</th><th>Time</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((b) => (
                <tr key={b.batchId}>
                  <td style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{b.batchId}</td>
                  <td>{b.inspected}</td>
                  <td style={{ color: 'var(--good)' }}>{b.ok}</td>
                  <td style={{ color: b.ng ? 'var(--bad)' : undefined }}>{b.ng}</td>
                  <td>{b.shift ?? '—'}</td>
                  <td>{new Date(b.startTime).toLocaleTimeString()}</td>
                  <td>{new Date(b.endTime).toLocaleTimeString()}</td>
                  <td>{b.minutes}m</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

interface DiagDevice { id: number; name: string; role: string; driver: string; enabled: boolean; category: string; manualActionable: boolean }
interface DiagData { total: number; cameras: DiagDevice[]; lighting: DiagDevice[]; other: DiagDevice[] }
/** The tenant's own line devices, grouped, with a manual action relayed to the controller. */
function PortalDiagnostic() {
  const { user } = useAuth();
  const canOp = canOperate(user?.features ?? null, 'live');
  const [d, setD] = useState<DiagData | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    fetch('/api/portal/diagnostic/devices')
      .then((r) => (r.ok ? r.json() : null))
      .then(setD)
      .catch(() => setD(null));
  }, []);
  if (d && d.total === 0) return null; // no devices → nothing to show
  const act = async (id: number, action: 'on' | 'off') => {
    setMsg(null);
    setErr(null);
    try {
      const res = await fetch(`/api/portal/diagnostic/devices/${id}/action`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ action }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || 'Action failed');
      setMsg('Sent to the controller.');
    } catch (e) {
      setErr((e as Error).message);
    }
  };
  const group = (title: string, items: DiagDevice[]) =>
    items.length ? (
      <div style={{ marginTop: 12 }}>
        <div className="muted" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '.05em' }}>
          {title} ({items.length})
        </div>
        {items.map((x) => (
          <div key={x.id} className="row" style={{ justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid var(--border)' }}>
            <span style={{ fontSize: 13 }}>
              {x.name} <span className="muted" style={{ fontSize: 11 }}>· {x.driver}</span>
            </span>
            {x.manualActionable && canOp && (
              <span className="row" style={{ gap: 6 }}>
                <button className="btn sm" onClick={() => void act(x.id, 'on')}>On</button>
                <button className="btn sm" onClick={() => void act(x.id, 'off')}>Off</button>
              </span>
            )}
          </div>
        ))}
      </div>
    ) : null;
  return (
    <div className="panel" style={{ marginTop: 20 }}>
      <strong>Diagnostic — devices</strong>
      {!d ? (
        <div className="muted" style={{ marginTop: 10 }}>Loading…</div>
      ) : (
        <>
          {group('Cameras', d.cameras)}
          {group('Lighting', d.lighting)}
          {group('Other', d.other)}
          {msg && <div style={{ color: 'var(--good)', fontSize: 12, marginTop: 8 }}>{msg}</div>}
          {err && <div style={{ color: 'var(--bad)', fontSize: 12, marginTop: 8 }}>{err}</div>}
          <div className="muted" style={{ fontSize: 11, marginTop: 10 }}>
            Actions are sent to your line controller; if the station is offline you'll get a clear error.
          </div>
        </>
      )}
    </div>
  );
}
